import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { checkLoginStatus, selectUserInfo, updateUserProfile, getUserById, clearAdminCache, checkAdminToken } from "../../../redux/WithcookieSlice";
import { useTranslation } from "../../../redux/TranslationHelper";
import { EditedUserInfo, UserInfo } from "./UserProfile.types";
import { adaptUserProfileProps } from "./UserProfile.adapter";

const UserProfileComponent: React.FC<any> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = adaptUserProfileProps(props);
  const {
    style = {},
    content = {},
    varFields = {
      var01: { 
        show: false, 
        label: "",
        type: "input"
      },
      var02: { 
        show: false, 
        label: "",
        type: "input"
      },
      var03: { 
        show: false, 
        label: "",
        type: "input"
      },
      var04: { 
        show: false, 
        label: "",
        type: "input"
      },
      var05: { 
        show: false, 
        label: "",
        type: "input"
      },
      var06: { 
        show: false, 
        label: "",
        type: "input"
      },
      var07: { 
        show: false, 
        label: "",
        type: "input"
      },
      var08: { 
        show: false, 
        label: "",
        type: "input"
      },
      var09: { 
        show: false, 
        label: "",
        type: "input"
      },
      var10: { 
        show: false, 
        label: "",
        type: "input"
      }
    },
    basicFields = {
      userId: true,
      name: true,
      password: true,
      phone: true,
      email: true,
      birthday: true,
      createdAt: true,
      address: true,
      referralLink: true
    }
  } = adaptedProps;
  
  const dispatch = useDispatch() as any;
  const userInfo = useSelector(selectUserInfo) as UserInfo;
  const userInfoLoading = useSelector((state: any) => state.withcookie.userInfoLoading);
  const user = useSelector((state: any) => state.withcookie.user);
  const { t } = useTranslation();
  const location = useLocation();
  
  // 에디터 모드 감지
  const isEditorMode = location.pathname.startsWith('/edit');
  
  // 속성 패널에서 설정할 수 있는 제목 (기본값: "회원 정보")
  const title = content.title || t('회원 정보');
  

  
  const [copied, setCopied] = useState(false);
  const [referralLink, setReferralLink] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editedUserInfo, setEditedUserInfo] = useState<EditedUserInfo>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isReloading, setIsReloading] = useState(false);
  const [highlightedFields, setHighlightedFields] = useState<Record<string, boolean>>({});
  const [showWithdrawalDialog, setShowWithdrawalDialog] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // withcookieData에서 테마 색상 가져오기
  const withcookieData = useSelector((state: any) => state.withcookie.withcookieData);
  const theme = withcookieData?.skin?.theme || {};
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#007bff";
  const secondaryColor = colorSet?.secondary || "#6c757d";
  const successColor = colorSet?.tertiary || "#28a745";

  // 스타일 설정
  const defaultStyle: React.CSSProperties = {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    fontFamily: 'Noto Sans KR, sans-serif',
    maxWidth: '800px',
    margin: '20px auto',
    position: 'relative',
    ...style
  };

  // 사용자 정보 조회 - 무한 루프 방지 개선
  useEffect(() => {
    // 에디터 모드에서는 사용자 정보 로드를 스킵
    if (isEditorMode) {
      return;
    }
    
    // 이미 사용자 정보가 있거나 로딩 중이면 스킵
    if (userInfo || userInfoLoading) {
      return;
    }
    
    // 기본 사용자 정보가 있으면 상세 정보 로드
    if (user?.id) {
      const userId = user.id;
      const companyId = user.company_id || 21;
      dispatch(getUserById({ userId, companyId }));
      setIsInitialized(true);
      return;
    }
  }, [dispatch, user, userInfo, userInfoLoading, isEditorMode]);

  // 추천링크 생성
  useEffect(() => {
    if (userInfo && userInfo.insungId) {
      const host = window.location.host;
      setReferralLink(`https://${host}/signup/${userInfo.insungId}`);
    }
  }, [userInfo]);

  // 수정 모드로 전환될 때 현재 사용자 정보로 편집 상태 초기화
  useEffect(() => {
    if (userInfo && isEditing) {
      setEditedUserInfo({
        name: userInfo.name || '',
        phone: userInfo.phone || '',
        email: userInfo.email || '',
        birthday: userInfo.birthdate || userInfo.birthday || '',
        address: userInfo.address || '',
        var01: userInfo.var01 || '',
        var02: userInfo.var02 || '',
        var03: userInfo.var03 || '',
        var04: userInfo.var04 || '',
        var05: userInfo.var05 || '',
        var06: userInfo.var06 || '',
        var07: userInfo.var07 || '',
        var08: userInfo.var08 || '',
        var09: userInfo.var09 || '',
        var10: userInfo.var10 || '',
        trxWithdrawAddress: userInfo.trxWithdrawAddress || '',
        bscWithdrawAddress: userInfo.bscWithdrawAddress || '',
      });
    }
  }, [isEditing, userInfo]);
  
  // 하이라이트 효과 초기화
  useEffect(() => {
    if (Object.keys(highlightedFields).length > 0) {
      const timer = setTimeout(() => {
        setHighlightedFields({});
      }, 3000); // 3초 후 하이라이트 효과 제거
      
      return () => clearTimeout(timer);
    }
  }, [highlightedFields]);

  // 컴포넌트 언마운트 시 캐시 정리
  useEffect(() => {
    return () => {
      // 컴포넌트가 언마운트될 때만 초기화 상태 리셋 (전역 캐시는 보존)
      setIsInitialized(false);
    };
  }, []);

  // 로딩 상태 타임아웃 처리 - 무한 로딩 방지
  useEffect(() => {
    if (userInfoLoading) {
      const timeout = setTimeout(() => {
        setIsInitialized(false);
        // 로딩이 너무 오래 걸리면 강제로 다시 시도
        if (!userInfo && user?.id) {
          const userId = user.id;
          const companyId = user.company_id || 21;
          dispatch(getUserById({ userId, companyId }));
        }
      }, 10000); // 10초 타임아웃

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [userInfoLoading, userInfo, user?.id, dispatch]);

  // 클립보드에 복사 기능
  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000); // 3초 후 메시지 숨김
      })
      .catch(err => {
        console.error(t('클립보드 복사 실패:'), err);
      });
  };

  // 회원 탈퇴 버튼 클릭 핸들러 (현재 사용 안함)
  // const handleWithdrawalClick = () => {
  //   setShowWithdrawalDialog(true);
  // };

  // 회원 탈퇴 확인 핸들러
  const handleWithdrawalConfirm = () => {
    setShowWithdrawalDialog(false);
    alert(t('회원 탈퇴를 원하시면 고객센터로 문의해주세요.'));
  };

  // 회원 탈퇴 취소 핸들러
  const handleWithdrawalCancel = () => {
    setShowWithdrawalDialog(false);
  };

  // 수정 시작
  const handleEdit = () => {
    setIsEditing(true);
    setSaveError(null);
  };

  // 수정 취소
  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  // 입력 필드 변경 처리
  const handleInputChange = (field: string, value: string) => {
    setEditedUserInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 사용자 정보 다시 로드 - 간소화 (현재 사용 안함)
  // const reloadUserInfo = async () => {
  //   setIsReloading(true);
  //   try {
  //     // 사용자 ID와 회사 ID 확인
  //     if (!userInfo || !userInfo.id) {
  //       throw new Error(t('사용자 정보가 없습니다. 로그인이 필요합니다.'));
  //     }
  //     
  //     const userId = userInfo.id;
  //     const companyId = userInfo.company_id || 21; 
  //     
  //     // getUserById 액션 디스패치 (단순화)
  //     await dispatch(getUserById({ 
  //       userId: typeof userId === 'string' ? parseInt(userId, 10) : userId, 
  //       companyId 
  //     }));
  //     
  //   } catch (error) {
  //     console.error(t("사용자 정보 로드 오류:"), error);
  //   } finally {
  //     setIsReloading(false);
  //   }
  // };

  // 수정 내용 저장
  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      // 디버깅: editedUserInfo 전체 내용 로그
      
      // 변경된 필드 확인
      const changedFields: Record<string, boolean> = {};
      if (userInfo) {
        if (editedUserInfo.name !== userInfo.name) changedFields.name = true;
        if (editedUserInfo.phone !== userInfo.phone) changedFields.phone = true;
        if (editedUserInfo.email !== userInfo.email) changedFields.email = true;
        if (editedUserInfo.birthday !== (userInfo.birthdate || userInfo.birthday)) changedFields.birthday = true;
        if (editedUserInfo.address !== userInfo.address) changedFields.address = true;
        if (editedUserInfo.var01 !== userInfo.var01) changedFields.var01 = true;
        if (editedUserInfo.var02 !== userInfo.var02) changedFields.var02 = true;
        if (editedUserInfo.var03 !== userInfo.var03) changedFields.var03 = true;
        if (editedUserInfo.var04 !== userInfo.var04) changedFields.var04 = true;
        if (editedUserInfo.var05 !== userInfo.var05) changedFields.var05 = true;
        if (editedUserInfo.var06 !== userInfo.var06) changedFields.var06 = true;
        if (editedUserInfo.var07 !== userInfo.var07) changedFields.var07 = true;
        if (editedUserInfo.var08 !== userInfo.var08) changedFields.var08 = true;
        if (editedUserInfo.var09 !== userInfo.var09) changedFields.var09 = true;
        if (editedUserInfo.var10 !== userInfo.var10) changedFields.var10 = true;
        if (editedUserInfo.password && editedUserInfo.password.trim() !== '') changedFields.password = true;
        if (editedUserInfo.trxWithdrawAddress !== userInfo.trxWithdrawAddress) changedFields.trxWithdrawAddress = true;
        if (editedUserInfo.bscWithdrawAddress !== userInfo.bscWithdrawAddress) changedFields.bscWithdrawAddress = true;
      }
      
      
      // updateUserProfile 액션을 통해 사용자 정보 업데이트
      const resultAction = await dispatch(updateUserProfile(editedUserInfo));
      
      
      if (updateUserProfile.fulfilled.match(resultAction)) {
        // 성공적으로 업데이트된 경우
        setIsEditing(false);
        
        // 관리자 캐시 초기화 (동기)
        dispatch(clearAdminCache());
        
        // 최신 사용자 정보 다시 조회
        if (userInfo?.id) {
          const userId = userInfo.id;
          const companyId = userInfo.company_id || 21;
          dispatch(getUserById({
            userId: typeof userId === 'string' ? parseInt(userId, 10) : userId, 
            companyId 
          })).catch((error: any) => {
            console.error('[UserProfile] 사용자 정보 재조회 실패:', error);
          });
        }
        
        // 변경된 필드 하이라이트 설정
        setHighlightedFields(changedFields);
        
        alert(t('회원 정보가 성공적으로 수정되었습니다.'));
      } else {
        // 오류 처리
        if (resultAction.payload) {
          setSaveError(resultAction.payload.message || t('회원 정보 수정 중 오류가 발생했습니다.'));
        } else {
          setSaveError(t('회원 정보 수정 중 오류가 발생했습니다.'));
        }
      }
    } catch (error) {
      console.error(t('프로필 업데이트 오류:'), error);
      setSaveError(t('회원 정보 수정 중 오류가 발생했습니다.'));
    } finally {
      setIsSaving(false);
    }
  };

  // 생년월일 포맷팅 함수
  const formatBirthdate = (date: string | Date | undefined) => {
    if (!date) return '-';
    
    // 날짜 문자열인 경우 처리
    if (typeof date === 'string') {
      // ISO 날짜 형식(YYYY-MM-DDTHH:mm:ss.sssZ)인 경우 날짜 부분만 추출
      if (date.includes('T')) {
        return date.split('T')[0];
      }
      
      // 이미 YYYY-MM-DD 형식이면 그대로 반환
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      
      // 다른 형식의 날짜 문자열인 경우 Date 객체로 변환 후 처리
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return t('Invalid date'); // 유효하지 않은 날짜면 원본 반환
        return dateObj.toISOString().split('T')[0];
      } catch (e) {
        return date; // 변환 실패시 원본 반환
      }
    }
    
    // Date 객체인 경우 처리
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    
    return String(date); // 다른 타입이면 문자열로 변환하여 반환
  };

  // var 필드 렌더링 함수
  const renderVarField = (fieldName: string, fieldConfig: any, currentValue: string | undefined, editedValue: string | undefined) => {
    const fieldType = fieldConfig.type || 'input';
    
    // 계좌번호와 주민등록번호 필드의 값을 분리하여 관리
    const fieldValue = editedValue || currentValue || '';
    const [bankCode = '', accountNumber = ''] = fieldValue.includes('|') ? fieldValue.split('|') : ['', ''];
    const [ssnFront = '', ssnBack = ''] = fieldValue.includes('-') ? fieldValue.split('-') : ['', ''];

    // 은행 코드를 은행명으로 변환하는 함수
    const getBankName = (code: string) => {
      const bankMap: Record<string, string> = {
        'KB': 'KB국민은행',
        'SH': '신한은행',
        'WR': '우리은행',
        'HN': '하나은행',
        'NH': 'NH농협은행',
        'KP': '우체국',
        'IBK': 'IBK기업은행',
        'KDB': 'KDB산업은행',
        'SC': 'SC제일은행',
        'CT': '씨티은행',
        'DG': '대구은행',
        'BS': '부산은행',
        'GJ': '광주은행',
        'JJ': '제주은행',
        'JB': '전북은행',
        'KN': '경남은행',
        'KK': '카카오뱅크',
        'TS': '토스뱅크'
      };
      return bankMap[code] || code;
    };

    return (
      <div style={profileRowStyle}>
        <div style={labelStyle}>{t(fieldConfig.label)}</div>
        <div style={valueStyle}>
          {isEditing ? (
            // 수정 모드
            fieldType === 'account' ? (
              <div style={{ display: 'flex', gap: '10px' }}>
                <select
                  style={{ ...inputStyle, flex: '0 0 150px' }}
                  value={bankCode || ''}
                  onChange={(e) => {
                    const newValue = `${e.target.value}|${accountNumber || ''}`;
                    handleInputChange(fieldName, newValue);
                  }}
                >
                  <option value="">{t('은행 선택')}</option>
                  <option value="KB">{t('KB국민은행')}</option>
                  <option value="SH">{t('신한은행')}</option>
                  <option value="WR">{t('우리은행')}</option>
                  <option value="HN">{t('하나은행')}</option>
                  <option value="NH">{t('NH농협은행')}</option>
                  <option value="KP">{t('우체국')}</option>
                  <option value="IBK">{t('IBK기업은행')}</option>
                  <option value="KDB">{t('KDB산업은행')}</option>
                  <option value="SC">{t('SC제일은행')}</option>
                  <option value="CT">{t('씨티은행')}</option>
                  <option value="DG">{t('대구은행')}</option>
                  <option value="BS">{t('부산은행')}</option>
                  <option value="GJ">{t('광주은행')}</option>
                  <option value="JJ">{t('제주은행')}</option>
                  <option value="JB">{t('전북은행')}</option>
                  <option value="KN">{t('경남은행')}</option>
                  <option value="KK">{t('카카오뱅크')}</option>
                  <option value="TS">{t('토스뱅크')}</option>
                </select>
                <input
                  type="text"
                  style={{ ...inputStyle, flex: '1' }}
                  placeholder={t('계좌번호 입력')}
                  value={accountNumber || ''}
                  onChange={(e) => {
                    const newValue = `${bankCode || ''}|${e.target.value}`;
                    handleInputChange(fieldName, newValue);
                  }}
                />
              </div>
            ) : fieldType === 'ssn' ? (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  style={{ ...inputStyle, flex: '1' }}
                  placeholder={t('앞자리 (6자리)')}
                  maxLength={6}
                  value={ssnFront || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    const newValue = `${value}-${ssnBack || ''}`;
                    handleInputChange(fieldName, newValue);
                  }}
                />
                <span style={{ fontSize: '20px', color: '#666' }}>-</span>
                <input
                  type="password"
                  style={{ ...inputStyle, flex: '1' }}
                  placeholder={t('뒷자리 (7자리)')}
                  maxLength={7}
                  value={ssnBack || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    const newValue = `${ssnFront || ''}-${value}`;
                    handleInputChange(fieldName, newValue);
                  }}
                />
              </div>
            ) : (
              // 기본 input 타입
              <input
                type="text"
                style={inputStyle}
                value={editedValue || ''}
                onChange={(e) => handleInputChange(fieldName, e.target.value)}
              />
            )
          ) : (
            // 보기 모드
            <div style={getHighlightedStyle(fieldName)}>
              {fieldType === 'account' && currentValue ? (
                // 계좌번호 표시: 은행명과 계좌번호를 보기 좋게 표시
                `${getBankName(bankCode)} ${accountNumber}`
              ) : fieldType === 'ssn' && currentValue ? (
                // 주민등록번호 표시: 전체 값을 보여줌
                currentValue
              ) : (
                currentValue || '-'
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // 프로필 정보 행 스타일
  const profileRowStyle = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '15px',
    marginBottom: '15px',
    width: '100%',
    borderBottom: '1px solid #eee',
    paddingBottom: '15px'
  };

  // 프로필 라벨 스타일
  const labelStyle = {
    flex: '0 0 120px',
    fontWeight: 'bold' as const,
    color: '#555'
  };

  // 프로필 값 스타일
  const valueStyle = {
    flex: '1 1 calc(100% - 135px)'
  };

  // 하이라이트된 값 스타일
  const getHighlightedStyle = (field: string) => {
    return highlightedFields[field] ? {
      backgroundColor: '#e6f7ff',
      padding: '5px',
      borderRadius: '4px',
      transition: 'background-color 0.5s ease',
      animation: 'fadeHighlight 3s'
    } : {};
  };

  // 입력 필드 스타일
  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px'
  };

  // 버튼 컨테이너 스타일
  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '20px'
  };

  // 수정 버튼 스타일
  const editButtonStyle = {
    padding: '8px 16px',
    backgroundColor: primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const,
    transition: 'background-color 0.2s'
  };

  // 취소 버튼 스타일
  const cancelButtonStyle = {
    padding: '8px 16px',
    backgroundColor: secondaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const,
    transition: 'background-color 0.2s'
  };

  // 저장 버튼 스타일
  const saveButtonStyle = {
    padding: '8px 16px',
    backgroundColor: successColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const,
    transition: 'background-color 0.2s',
    opacity: isSaving ? 0.7 : 1
  };

  // 오류 메시지 스타일
  const errorMessageStyle = {
    color: '#dc3545',
    marginTop: '10px',
    fontSize: '14px',
    textAlign: 'center' as const
  };

  // 로딩 오버레이 스타일 - React.CSSProperties로 선언시 에러가 발생하므로 as any로 타입 단언
  const loadingOverlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
    borderRadius: '8px'
  } as any; // 타입 에러 해결을 위해 any로 타입 단언

  // 추천링크 스타일
  const referralBoxStyle = {
    marginTop: '30px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px',
    border: '1px solid #ddd'
  };

  // 추천링크 헤더 스타일
  const referralHeaderStyle = {
    fontSize: '16px',
    fontWeight: 'bold' as const,
    marginBottom: '10px',
    color: '#333'
  };

  // 추천링크 인풋 스타일
  const referralInputStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  // 추천링크 텍스트 필드 스타일
  const linkFieldStyle = {
    flex: '1',
    padding: '10px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#f1f3f5',
    color: '#495057',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const
  };

  // 복사 버튼 스타일
  const copyButtonStyle = {
    padding: '10px 15px',
    backgroundColor: copied ? successColor : primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const,
    transition: 'background-color 0.2s'
  };

  // 회원 탈퇴 버튼 스타일 (현재 사용 안함)
  // const withdrawalButtonStyle = {
  //   marginTop: '20px',
  //   padding: '10px 20px',
  //   backgroundColor: '#dc3545',
  //   color: 'white',
  //   border: 'none',
  //   borderRadius: '4px',
  //   cursor: 'pointer',
  //   fontSize: '14px',
  //   fontWeight: 'bold' as const,
  //   transition: 'background-color 0.2s',
  //   width: '100%'
  // };

  // 다이얼로그 오버레이 스타일
  const dialogOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  } as any;

  // 다이얼로그 박스 스타일
  const dialogBoxStyle = {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    maxWidth: '400px',
    width: '90%',
    textAlign: 'center' as const
  };

  // 다이얼로그 제목 스타일
  const dialogTitleStyle = {
    fontSize: '18px',
    fontWeight: 'bold' as const,
    marginBottom: '20px',
    color: '#333'
  };

  // 다이얼로그 메시지 스타일
  const dialogMessageStyle = {
    fontSize: '14px',
    color: '#666',
    marginBottom: '30px',
    lineHeight: '1.6'
  };

  // 다이얼로그 버튼 컨테이너 스타일
  const dialogButtonContainerStyle = {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center'
  };

  // 다이얼로그 확인 버튼 스타일
  const dialogConfirmButtonStyle = {
    padding: '10px 30px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const
  };

  // 다이얼로그 취소 버튼 스타일
  const dialogCancelButtonStyle = {
    padding: '10px 30px',
    backgroundColor: secondaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const
  };

  // 로딩 상태 또는 로그인 안 된 경우 처리
  if (!userInfo) {
    if (userInfoLoading) {
      return (
        <div style={defaultStyle as any}>
          <h2 style={{ marginTop: 0, marginBottom: '20px', textAlign: 'center' }}>{t(title)}</h2>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>{t('회원 정보를 불러오는 중...')}</p>
          </div>
        </div>
      );
    }
    
    return (
      <div style={defaultStyle as any}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', textAlign: 'center' }}>{t(title)}</h2>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>{t('로그인이 필요합니다.')}</p>
          <button 
            onClick={() => {
              setIsInitialized(false);
              dispatch(checkLoginStatus({ forceRefresh: true }));
            }}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {t('다시 시도')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={defaultStyle as any}>
      {(isSaving || isReloading) && (
        <div style={loadingOverlayStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '10px', fontSize: '20px' }}>
              {isSaving ? t('저장 중...') : t('정보 로드 중...')}
            </div>
            <div>{t('잠시만 기다려주세요.')}</div>
          </div>
        </div>
      )}
      
      <h2 style={{ marginTop: 0, marginBottom: '20px', textAlign: 'center' }}>{t(title)}</h2>
      
      {/* 수정/저장 버튼 */}
      <div style={buttonContainerStyle}>
        {isEditing ? (
          <>
            <button 
              onClick={handleCancel} 
              style={cancelButtonStyle}
              disabled={isSaving || isReloading}
            >
              {t('취소')}
            </button>
            <button 
              onClick={handleSave} 
              style={saveButtonStyle}
              disabled={isSaving || isReloading}
            >
              {isSaving ? t('저장 중...') : t('저장')}
            </button>
          </>
        ) : (
          <button 
            onClick={handleEdit} 
            style={editButtonStyle}
            disabled={isSaving || isReloading}
          >
            {t('수정')}
          </button>
        )}
      </div>
      
      {/* 에러 메시지 */}
      {saveError && (
        <div style={errorMessageStyle}>{saveError}</div>
      )}
      
      {/* 유저 정보 섹션 */}
      <div style={{ marginBottom: '30px' }}>
        {/* 아이디 */}
        {basicFields.userId && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('아이디')}</div>
            <div style={valueStyle}>{userInfo.insungId}</div>
          </div>
        )}
        
        {/* 이름 */}
        {basicFields.name && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('이름')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="text"
                  style={inputStyle}
                  value={editedUserInfo.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('name')}>{userInfo.name}</div>
              )}
            </div>
          </div>
        )}
        
        {/* 비밀번호 */}
        {basicFields.password && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('비밀번호')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="password"
                  style={inputStyle}
                  placeholder={t('새 비밀번호 입력 (변경하지 않으려면 빈칸으로 두세요)')}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('password')}>••••••••</div>
              )}
            </div>
          </div>
        )}
        
        {/* 전화번호 */}
        {basicFields.phone && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('전화번호')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="tel"
                  style={inputStyle}
                  value={editedUserInfo.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('phone')}>{userInfo.phone}</div>
              )}
            </div>
          </div>
        )}
        
        {/* 이메일 */}
        {basicFields.email && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('이메일')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="email"
                  style={inputStyle}
                  value={editedUserInfo.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('email')}>{userInfo.email || '-'}</div>
              )}
            </div>
          </div>
        )}
        
        {/* 생년월일 */}
        {basicFields.birthday && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('생년월일')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="date"
                  style={inputStyle}
                  value={editedUserInfo.birthday || ''}
                  onChange={(e) => handleInputChange('birthday', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('birthday')}>
                  {formatBirthdate(userInfo.birthdate || userInfo.birthday)}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 가입일 */}
        {basicFields.createdAt && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('가입일')}</div>
            <div style={valueStyle}>
              {userInfo.created_at ? new Date(userInfo.created_at).toLocaleDateString('ko-KR') : '-'}
            </div>
          </div>
        )}
        
        {/* 주소 */}
        {basicFields.address && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('주소')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="text"
                  style={inputStyle}
                  value={editedUserInfo.address || ''}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('address')}>{userInfo.address || '-'}</div>
              )}
            </div>
          </div>
        )}

        {/* var01~var10 커스텀 필드 (보여줄 필드가 있는 경우만 행 렌더링) */}
        {varFields && varFields.var01?.show && renderVarField('var01', varFields.var01, userInfo.var01, editedUserInfo.var01)}
        {varFields && varFields.var02?.show && renderVarField('var02', varFields.var02, userInfo.var02, editedUserInfo.var02)}
        {varFields && varFields.var03?.show && renderVarField('var03', varFields.var03, userInfo.var03, editedUserInfo.var03)}
        {varFields && varFields.var04?.show && renderVarField('var04', varFields.var04, userInfo.var04, editedUserInfo.var04)}
        {varFields && varFields.var05?.show && renderVarField('var05', varFields.var05, userInfo.var05, editedUserInfo.var05)}
        {varFields && varFields.var06?.show && renderVarField('var06', varFields.var06, userInfo.var06, editedUserInfo.var06)}
        {varFields && varFields.var07?.show && renderVarField('var07', varFields.var07, userInfo.var07, editedUserInfo.var07)}
        {varFields && varFields.var08?.show && renderVarField('var08', varFields.var08, userInfo.var08, editedUserInfo.var08)}
        {varFields && varFields.var09?.show && renderVarField('var09', varFields.var09, userInfo.var09, editedUserInfo.var09)}
        {varFields && varFields.var10?.show && renderVarField('var10', varFields.var10, userInfo.var10, editedUserInfo.var10)}
        
        {/* 지갑 주소 필드 (companyId === 24 일 경우만 표시) */}
        {(withcookieData?.skin?.companyId === 24 || withcookieData?.skin?.companyId === 26 || withcookieData?.skin?.companyId === 27) && (
          <>

            {(withcookieData?.skin?.companyId === 24) && (
              <>
                {/* TRX 지갑 주소 */}
                <div style={profileRowStyle}>
                <div style={labelStyle}>{t('TRX 지갑주소')}</div>
                <div style={valueStyle}>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        style={{
                          ...inputStyle,
                          backgroundColor: userInfo.trxWithdrawAddress ? '#f5f5f5' : '#ffffff',
                          cursor: userInfo.trxWithdrawAddress ? 'not-allowed' : 'text'
                        }}
                        value={editedUserInfo.trxWithdrawAddress || ''}
                        onChange={(e) => handleInputChange('trxWithdrawAddress', e.target.value)}
                        disabled={userInfo.trxWithdrawAddress ? true : false}
                        placeholder={userInfo.trxWithdrawAddress ? t('이미 설정된 주소는 수정할 수 없습니다') : t('TRX 지갑주소 입력')}
                      />
                      {userInfo.trxWithdrawAddress && (
                        <div style={{color: '#ff9800', fontSize: '12px', marginTop: '5px'}}>
                          {t('TRX 주소는 이미 설정되어 수정할 수 없습니다.')}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={getHighlightedStyle('trxWithdrawAddress')}>
                      {userInfo.trxWithdrawAddress || '-'}
                    </div>
                  )}
                </div>
              </div>
              </>
            )}


            {/* BSC 지갑 주소 */}
            <div style={profileRowStyle}>
              <div style={labelStyle}>{t('USDT-BNB 지갑주소')}</div>
              <div style={valueStyle}>
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      style={{
                        ...inputStyle,
                        backgroundColor: userInfo.bscWithdrawAddress ? '#f5f5f5' : '#ffffff',
                        cursor: userInfo.bscWithdrawAddress ? 'not-allowed' : 'text'
                      }}
                      value={editedUserInfo.bscWithdrawAddress || ''}
                      onChange={(e) => handleInputChange('bscWithdrawAddress', e.target.value)}
                      disabled={userInfo.bscWithdrawAddress ? true : false}
                      placeholder={userInfo.bscWithdrawAddress ? t('이미 설정된 주소는 수정할 수 없습니다') : t('USDT-BNB 지갑주소 입력')}
                    />
                    {userInfo.bscWithdrawAddress && (
                      <div style={{color: '#ff9800', fontSize: '12px', marginTop: '5px'}}>
                        {t('BSC 주소는 이미 설정되어 수정할 수 없습니다.')}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={getHighlightedStyle('bscWithdrawAddress')}>
                    {userInfo.bscWithdrawAddress || '-'}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* 추천링크 박스 */}
      {basicFields.referralLink && (
        <div style={referralBoxStyle}>
          <div style={referralHeaderStyle}>{t('추천링크')}</div>
          <div style={referralInputStyle}>
            <div style={linkFieldStyle}>{referralLink}</div>
            <button 
              onClick={copyToClipboard} 
              style={copyButtonStyle}
              title={t('클립보드에 링크 복사')}
            >
              {copied ? t('복사됨!') : t('복사')}
            </button>
          </div>
          {copied && (
            <div style={{ color: 'green', fontSize: '12px', marginTop: '5px' }}>
              {t('클립보드에 복사되었습니다!')}
            </div>
          )}
        </div>
      )}
      
      {/* 회원 탈퇴 버튼 */}
      {/* <button
        onClick={handleWithdrawalClick}
        style={withdrawalButtonStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#c82333';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#dc3545';
        }}
      >
        {t('회원 탈퇴')}
      </button> */}

      {/* 회원 탈퇴 확인 다이얼로그 */}
      {showWithdrawalDialog && (
        <div style={dialogOverlayStyle}>
          <div style={dialogBoxStyle}>
            <div style={dialogTitleStyle}>{t('회원 탈퇴')}</div>
            <div style={dialogMessageStyle}>
              {t('정말로 회원 탈퇴를 하시겠습니까?')}<br />
              {t('탈퇴 시 모든 혜택과 포인트가 소멸되며 복구가 불가능합니다.')}
            </div>
            <div style={dialogButtonContainerStyle}>
              <button
                onClick={handleWithdrawalCancel}
                style={dialogCancelButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                {t('아니오')}
              </button>
              <button
                onClick={handleWithdrawalConfirm}
                style={dialogConfirmButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                {t('예')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileComponent; 